<template>
  <div>
    <b-card title="My Refund Queue">
      <!-- search input -->
      <b-row v-if="hasData" class="mb-1">
        <b-col class="d-flex justify-content-start">
          <b-form-group>
            <b-form-input
              v-model="searchTerm"
              placeholder="Search"
              type="search"
            />
          </b-form-group>
        </b-col>
        <b-col />
        <b-col class="d-flex justify-content-end">
          <xlsx-workbook>
            <xlsx-sheet
              v-for="sheet in sheets"
              :key="sheet.name"
              :collection="sheet.data"
              :sheet-name="sheet.name"
            />
            <xlsx-download>
              <b-button variant="primary" class="d-flex">
                <feather-icon icon="DownloadCloudIcon" /> Export
              </b-button>
            </xlsx-download>
          </xlsx-workbook>
        </b-col>
      </b-row>

      <!-- table -->
      <vue-good-table
        :columns="columns"
        :rows="rows"
        :rtl="direction"
        :search-options="{
          enabled: true,
          externalQuery: searchTerm,
        }"
        :pagination-options="{
          enabled: true,
          perPage: pageLength,
        }"
      >
        <template slot="table-row" slot-scope="props">
          <!-- Column: Name -->

          <!-- Column: Status -->
          <span v-if="props.column.field === 'refundStatus'">
            <span>
              <b-badge v-if="props.row.refundStatus === 0" pill variant="info">
                Not Requested
              </b-badge>
              <b-badge
                v-else-if="props.row.refundStatus === 1"
                pill
                variant="warning"
                class="text-light"
              >
                Pending
              </b-badge>
              <b-badge
                v-else-if="props.row.refundStatus === 2"
                pill
                variant="light-success"
                class="text-light"
              >
                Submitted
              </b-badge>
              <b-badge
                v-else-if="props.row.refundStatus === 3"
                pill
                variant="success"
                class="text-light"
              >
                Approved
              </b-badge>
              <b-badge
                v-else-if="props.row.refundStatus === 4"
                pill
                variant="light-info"
                class="text-light"
              >
                Sent
              </b-badge>
              <b-badge
                v-else-if="props.row.refundStatus === 5"
                pill
                variant="danger"
                class="text-light"
              >
                Rejected
              </b-badge>
              <b-badge
                v-else-if="props.row.refundStatus === 6"
                pill
                variant="primary"
                class="text-light"
              >
                Completed
              </b-badge>
              <b-badge v-else pill variant="info" class="text-light">
                {{ props.row.refundStatus }}
              </b-badge>
            </span>
          </span>

          <!-- Column: Action -->
          <span v-else-if="props.column.field === 'action'">
            <!-- <b-row v-if="props.row.paymentStatus !== 3"> -->
            <!-- v-else-if="props.row.Payment_Status==='pending'" -->
            <!-- <b-dropdown
                variant="link"
                no-caret
                :right="$store.state.appConfig.isRTL"
              >
                <template v-slot:button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="16"
                    class="text-body align-middle mr-25"
                  />
                </template> -->

            <b-button
              :to="{
                name: 'refund-handler',
                params: {
                  refundQueue: props.row,
                  id: props.row.id,
                },
              }"
              variant="primary"
              class="d-flex"
            >
              <feather-icon icon="ActivityIcon" />
              <span class="align-middle ml-50 text-light">Approve/Reject</span>
            </b-button>
            <!-- </b-dropdown>
            </b-row> -->
          </span>

          <span v-else-if="props.column.field === 'paymentStatusDescription'">
            <span>
              <b-badge
                v-if="props.row.paymentStatus === 2"
                pill
                variant="success"
              >
                Approved
              </b-badge>
              <b-badge
                v-else-if="props.row.paymentStatus === 3"
                pill
                variant="danger"
              >
                Canceled
              </b-badge>
              <b-badge
                v-else-if="props.row.paymentStatus === 1"
                pill
                variant="warning"
              >
                Pending
              </b-badge>
              <b-badge v-else pill variant="info">
                {{ props.row.paymentStatusDescription }}
              </b-badge>
            </span>
          </span>

          <!-- Column: Common -->
          <span v-else>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template>

        <!-- pagination -->
        <template slot="pagination-bottom" slot-scope="props">
          <div class="d-flex justify-content-between flex-wrap">
            <div class="d-flex align-items-center mb-0 mt-1">
              <span class="text-nowrap"> Showing 1 to </span>
              <b-form-select
                v-model="pageLength"
                :options="['5', '10', '15', '30', '50']"
                class="mx-1"
                @input="
                  (value) => props.perPageChanged({ currentPerPage: value })
                "
              />
              <span class="text-nowrap"> of {{ props.total }} entries </span>
            </div>
            <div>
              <b-pagination
                :value="1"
                :total-rows="props.total"
                :per-page="pageLength"
                first-number
                last-number
                align="right"
                prev-class="prev-item"
                next-class="next-item"
                class="mt-1 mb-0"
                @input="(value) => props.pageChanged({ currentPage: value })"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </div>
          </div>
        </template>
      </vue-good-table>

      <!-- loadings -->
      <b-overlay :show="isLoading" no-wrap rounded="sm" />
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BAvatar,
  BBadge,
  BPagination,
  BOverlay,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BDropdown,
  BDropdownItem,
  BRow,
  BButton,
  BLink,
  BCol,
} from "bootstrap-vue";
import { VueGoodTable } from "vue-good-table";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import axios from "@axios";
import store from "@/store";
import XlsxSheet from "vue-xlsx/dist/components/XlsxSheet";
import XlsxDownload from "vue-xlsx/dist/components/XlsxDownload";
import XlsxWorkbook from "vue-xlsx/dist/components/XlsxWorkbook";

export default {
  name: "Users",
  components: {
    BRow,
    BCard,
    VueGoodTable,
    BAvatar,
    BBadge,
    BOverlay,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BDropdown,
    BDropdownItem,
    BButton,
    BLink,
    BCol,
    XlsxWorkbook,
    XlsxSheet,
    XlsxDownload,
  },
  data() {
    const nowDate = new Date();
    return {
      isLoading: false,
      hasData: false,
      rows: [],
      pageLength: 5,
      isDeleteModel: false,
      dir: false,
      rangeDate: null,
      activeRow: null,
      extendedDate: `${nowDate.getUTCFullYear()}-${
        nowDate.getUTCMonth() + 1
      }-${nowDate.getUTCDate()}`,
      orderDateRange: "",
      trxnDateRange: "",
      orderStartDate: "",
      trxnStartDate: "",
      orderEndDate: "",
      trxnEndDate: "",
      orderFirstName: "",
      orderLastName: "",
      orderEmail: "",
      orderPhone: "",
      orderAmount: "",
      orderCurrency: "",
      orderCity: "",
      orderCountry: "",
      orderChannel: "",
      selectedOrder: "",
      filterOrderId: "",
      filterFopId: 0,
      filterFirstName: "",
      filterLastName: "",
      filtertrnxDate: "",
      filterPhone: "",
      filterTraceNumber: "",
      filterToAmmount: 0,
      filterFromAmmount: 0,
      filterStatus: 0,
      columns: [
        {
          label: "Refund Requested Date",
          field: "refundRequestedDateString",
        },
        {
          label: "First Name",
          field: "firstName",
        },
        {
          label: "Last Name",
          field: "lastName",
        },
        {
          label: "Custom Order Code",
          field: "customOrderCode",
        },
        {
          label: "FlyGateOrder Id",
          field: "flyGateOrderId",
        },
        {
          label: "Refund Reference Code",
          field: "refundReferenceCode",
        },
        {
          label: "Refund Merchant Request Code",
          field: "refundMerchantRequestCode",
        },
        {
          label: "Paid FOP",
          field: "paidFOP",
        },
        {
          label: "Vendor TxnRef. Number",
          field: "vendorTxnRefNumber",
        },
        {
          label: "Original Ticket Number",
          field: "originalTicketNumber",
        },
        {
          label: "Status",
          field: "refundStatus",
        },
        {
          label: "Refundable Amount ",
          field: "refundRequestedAmount",
          type: "number",
        },
        {
          label: "Action",
          field: "action",
        },
      ],
      paymentOptions: [{ value: 0, text: "--select Payment Option--" }],
      statusOptions: [
        { value: "0", text: "All" },
        { value: "1", text: "Pending" },
        { value: "2", text: "Approved" },
        { value: "3", text: "Canceled" },
      ],
      currencyOptions: [{ value: "ETB", text: "ETB" }],
      selectedCurrency: "ETB",
      searchTerm: "",
      editabelData: {},
      sheets: [{ name: "Detail Refund Requests", data: [] }],
    };
  },
  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true;
        return this.dir;
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false;
      return this.dir;
    },
    statusVariant() {
      const statusColor = {
        /* eslint-disable key-spacing */
        1: "light-success",
        0: "light-danger",
        /* eslint-enable key-spacing */
      };

      return (status) => statusColor[status];
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    fetchData() {
      this.$store.dispatch("config/getActionPrivilege", "Refund-MyQueue");
      this.isLoading = true;
      axios
        .get("/Order/api/V1.0/Refund/GetMyRefundQueue")
        .then((res) => {
          this.sheets[0].data = [];
          this.rows = res.refundRequests;
          res.refundRequests.map((m) => {
            this.sheets[0].data.push({
              "Refund Requested Date": m.refundRequestedDateString,
              "First Name": m.firstName,
              "Last Name": m.lastName,
              Email: m.email,
              "Custom Order Code": m.customOrderCode,
              "FlyGate OrderId": m.flyGateOrderId,
              "Refund Reference Code": m.refundReferenceCode,
              "Paid FOP": m.paidFOP,
              "Vendor TxnRef. Number": m.vendorTxnRefNumber,
              "Refund Requested Amount": m.refundRequestedAmount,
              "Paid Date": m.paidDate,
              "Original Ticket Number": m.originalTicketNumber,
              "Refund Account Number": m.refundAccountNumber,
              "Refund Status":
                m.refundStatus == 0
                  ? "Refund Not Requested"
                  : m.refundStatus == 1
                  ? "Refund Pending"
                  : m.refundStatus == 2
                  ? "Refund Submitted"
                  : m.refundStatus == 3
                  ? "Refund Approved"
                  : m.refundStatus == 4
                  ? "Refund Sent"
                  : m.refundStatus == 5
                  ? "Refund Rejected"
                  : m.refundStatus == 6
                  ? "Refund Completed"
                  : null,
            });
          });
          this.hasData = true;
          this.isLoading = false;
        })
        .catch((res) => {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Error",
              icon: "BellIcon",
              variant: "danger",
              text: `${res.data.message}`,
            },
          });
          this.isLoading = false;
        });
    },
    cancelOrder(row) {
      this.$bvModal
        .msgBoxConfirm(`to Cancel Order Id  ${row.orderId}`, {
          title: "Are You Sure",
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          headerBgVariant: "danger",
          okTitle: "YES",
          cancelTitle: "NO",
          footerClass: "p-2",
          hideHeaderClose: true,
          centered: true,
        })
        .then((value) => {
          if (value) {
            this.isLoading = true;
            axios
              .post("/Order/api/V1.0/Orders/CancelOrder", {
                orderId: row.orderId,
              })
              .then((res) => {
                // const orderSelected = this.rows.filter(item => item.orderId === row.orderId)
                // console.log(orderSelected)

                this.$swal({
                  icon: "success",
                  title: "Status Updated!",
                  text: res.message,
                  customClass: {
                    confirmButton: "btn btn-success",
                  },
                });
                this.isLoading = false;
                this.filterOrders();
              })
              .catch((res) => {
                this.isLoading = false;
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: "Error",
                    icon: "BellIcon",
                    variant: "danger",
                    text: `${res.data.message}`,
                  },
                });
                // this.$refs.loginForm.setErrors(error)
              });
          }
        });
    },
    approveOrder(row) {
      this.$bvModal
        .msgBoxConfirm(`to Approve  ${row.orderId}`, {
          title: "Are You Sure",
          size: "sm",
          buttonSize: "sm",
          okVariant: "primary",
          headerBgVariant: "success",
          okTitle: "YES",
          cancelTitle: "NO",
          footerClass: "p-2",
          hideHeaderClose: true,
          centered: true,
        })
        .then((value) => {
          if (value) {
            this.isLoading = true;
            axios
              .post("/Order/api/V1.0/Orders/ApproveOrder", {
                orderId: row.orderId,
              })
              .then((res) => {
                this.fetchData();
                this.$swal({
                  icon: "success",
                  title: "Order Approved!",
                  text: res.message,
                  customClass: {
                    confirmButton: "btn btn-success",
                  },
                });
                this.isLoading = false;
                this.filterOrders();
              })
              .catch((res) => {
                this.isLoading = false;
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: "Error",
                    icon: "BellIcon",
                    variant: "danger",
                    text: `${res.data.message}`,
                  },
                });
                // this.$refs.loginForm.setErrors(error)
              });
          }
        });
    },
    resendOrder(row) {
      this.$bvModal
        .msgBoxConfirm(` to Resend  ${row.orderId}`, {
          title: "Are You Sure",
          size: "sm",
          buttonSize: "sm",
          okVariant: "primary",
          headerBgVariant: "primary",
          okTitle: "YES",
          cancelTitle: "NO",
          footerClass: "p-2",
          hideHeaderClose: true,
          centered: true,
        })
        .then((value) => {
          if (value) {
            this.isLoading = true;
            axios
              .post("/Order/api/V1.0/Orders/ResendOrder", {
                orderId: row.orderId,
              })
              .then((res) => {
                this.$swal({
                  icon: "success",
                  title: "Order Resended!",
                  text: res.message,
                  customClass: {
                    confirmButton: "btn btn-success",
                  },
                });
                this.isLoading = false;
                this.filterOrders();
              })
              .catch((res) => {
                this.isLoading = false;
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: "Error",
                    icon: "BellIcon",
                    variant: "danger",
                    text: `${res.data.message}`,
                  },
                });
                // this.$refs.loginForm.setErrors(error)
              });
          }
        });
    },
    extendOrder(row) {
      this.selectedOrder = row.orderId;
      this.$bvModal.show("modal-extend-orders");
    },
    submitExtendedDate() {
      this.isLoading = true;
      axios
        .post("/Order/api/V1.0/Orders/ExtendOrder", {
          orderId: this.selectedOrder,
          expiryDate: this.extendedDate,
        })
        .then((res) => {
          this.$swal({
            icon: "success",
            title: "Order Extended!",
            text: res.message,
            customClass: {
              confirmButton: "btn btn-success",
            },
          });
          this.isLoading = false;
          this.filterOrders();
        })
        .catch((res) => {
          this.isLoading = false;
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Error",
              icon: "BellIcon",
              variant: "danger",
              text: `${res.data.message}`,
            },
          });
          // this.$refs.loginForm.setErrors(error)
        });
    },
    filterOrders() {
      if (this.orderDateRange !== "") {
        const [orderSttDate, orderEdDate] = this.orderDateRange.split(" to ");
        this.orderStartDate = `${orderSttDate.trim()}`;
        this.orderEndDate = `${orderEdDate.trim()}`;
      }

      if (this.trxnDateRange !== "") {
        const [trxnSttDate, trxnEdDate] = this.trxnDateRange.split(" to ");
        this.trxnEndDate = `${trxnEdDate.trim()}`;
        this.trxnStartDate = `${trxnSttDate.trim()}`;
      }
      const url = `/Order/api/V1.0/Orders/FilterOrder?OrderId=${this.filterOrderId}&CustomerFirstName=${this.filterFirstName}&CustomerLastName=${this.filterLastName}&CustomerPhone=${this.filterPhone}&FromDate=${this.orderStartDate}&ToDate=${this.orderEndDate}&FromPaidDate=${this.trxnStartDate}&ToPaidDate=${this.trxnEndDate}&Currency=${this.selectedCurrency}&TraceNumber=${this.filterTraceNumber}&FromAmount=${this.filterFromAmmount} &ToAmount=${this.filterToAmmount}&paymentStatus=${this.filterStatus}&PaymentOptionId=${this.filterFopId}`;
      // Api call
      this.isLoading = true;
      axios
        .get(url)
        .then((res) => {
          this.rows = res.orders;
          this.isLoading = false;
          this.orderStartDate = "";
          this.orderEndDate = "";
          this.trxnEndDate = "";
          this.trxnStartDate = "";
        })
        .catch((res) => {
          this.isLoading = false;
          this.rows = [];
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Error",
              icon: "BellIcon",
              variant: "danger",
              text: `${res.data.message}`,
            },
          });
          // this.$refs.loginForm.setErrors(error)
          this.orderStartDate = "";
          this.orderEndDate = "";
          this.trxnEndDate = "";
          this.trxnStartDate = "";
        });
    },
  },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";
</style>
<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
